// Who's signed in. `me` is undefined while loading and null when signed out.
// The last confirmed account is kept on the device (see cache.ts), so the app draws at once on the next start while
// /api/me checks it; if the answer is a different account, or none, the device cache is wiped first.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { Me } from "@bloom/shared";
import { api, ApiError, auth } from "./api.ts";
import { cachedMe, confirmMe, wipeAll } from "./cache.ts";
import { rememberAvatars } from "./people.ts";
import { getThemePref, setThemePref, themePicks } from "./theme.ts";
import { getMessageDisplay, setMessageDisplay } from "./density.ts";
import { getTextSize, setTextSize } from "./textSize.ts";
import { getChannelActivity, setChannelActivity } from "./channelActivity.ts";
import { getReactionEffects, setReactionEffects } from "./reactionEffects.ts";
import { getStripTracking, setStripTracking } from "./stripTracking.ts";
import { linkPreviewPicks, takeAccountLinkPreviews } from "./linkPreviews.ts";
import { favoriteChangesMade, takeAccountFavorites } from "./favoriteEmoji.ts";
import { getTimestampFormat, setTimestampFormat } from "./timestampFormat.ts";
import { SIGNED_OUT_EVENT } from "./realtime.ts";
import { forgetVisits } from "./lastVisit.ts";
import { dropShare } from "./share.ts";
import { setAnalyticsUser } from "./analytics.ts";
import { dropPushSubscription, registerPush } from "./push.ts";

interface SessionValue {
  me: Me | null | undefined;
  refresh: () => Promise<Me | null>;
  /** Signs out and forgets everything this device kept for the account. */
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue>({ me: undefined, refresh: async () => null, signOut: async () => {} });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null | undefined>(() => cachedMe());
  const refresh = useCallback(async () => {
    try {
      const picksBefore = themePicks();
      const favoritesBefore = favoriteChangesMade();
      const linkPicksBefore = linkPreviewPicks();
      const m = await api<Me>("/me");
      rememberAvatars([m]);
      // The theme saved on my account wins over this device's copy (it may have changed on another device).
      // (themeName is the full choice; a server from before named themes only sends theme.)
      const theme = m.settings ? (m.settings.themeName ?? m.settings.theme) : undefined;
      if (theme && theme !== getThemePref() && themePicks() === picksBefore) setThemePref(theme, { save: false });
      if (m.settings?.messageDisplay && m.settings.messageDisplay !== getMessageDisplay()) setMessageDisplay(m.settings.messageDisplay, { save: false });
      if (m.settings?.textSize && m.settings.textSize !== getTextSize()) setTextSize(m.settings.textSize, { save: false });
      if (typeof m.settings?.channelActivity === "boolean" && m.settings.channelActivity !== getChannelActivity()) setChannelActivity(m.settings.channelActivity, { save: false });
      if (m.settings?.timestampFormat && m.settings.timestampFormat !== getTimestampFormat()) setTimestampFormat(m.settings.timestampFormat, { save: false });
      if (typeof m.settings?.reactionEffects === "boolean" && m.settings.reactionEffects !== getReactionEffects()) setReactionEffects(m.settings.reactionEffects, { save: false });
      if (typeof m.settings?.stripTracking === "boolean" && m.settings.stripTracking !== getStripTracking()) setStripTracking(m.settings.stripTracking, { save: false });
      // Unless I picked a mirror here since asking (then this device's pick is newer).
      takeAccountLinkPreviews(m.settings?.linkPreviews, linkPicksBefore);
      // Unless I starred or unstarred something here since asking (then this device's list is newer).
      takeAccountFavorites(m.settings?.favoriteEmoji, favoritesBefore);
      await confirmMe(m);
      setMe(m);
      return m;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        await confirmMe(null);
        setMe(null);
        return null;
      }
      throw e;
    }
  }, []);
  const signOut = useCallback(async () => {
    // The cache goes first: if signing out fails halfway, nothing of this account is left on the device.
    await wipeAll();
    forgetVisits();
    dropShare();
    // This browser stops getting the account's pushes; it needs the cookie, so before the sign-out (#230).
    await dropPushSubscription().catch(() => {});
    await auth.signOut().catch(() => {});
    setMe(null);
  }, []);
  useEffect(() => {
    // Offline with a cached account: keep showing it (the Room works from the cache and reconnects later).
    refresh().catch(() => {});
    // A room closed our socket because this sign-in ended: find out (it'll be null, which wipes the device cache)
    // so pages send us to sign in.
    const ended = () => void refresh().catch(() => {});
    addEventListener(SIGNED_OUT_EVENT, ended);
    return () => removeEventListener(SIGNED_OUT_EVENT, ended);
  }, [refresh]);
  // Usage events count a person by a hash of their account id, and forget them on sign-out (analytics.ts).
  const who = me === undefined ? undefined : (me?.id ?? null);
  useEffect(() => {
    if (who !== undefined) setAnalyticsUser(who);
  }, [who]);
  // A push subscription follows the sign-in session (#230): after a sign-in, save this browser's under the new one.
  useEffect(() => {
    if (who) void registerPush().catch(() => {});
  }, [who]);
  return <SessionContext.Provider value={{ me, refresh, signOut }}>{children}</SessionContext.Provider>;
}

export const useSession = () => useContext(SessionContext);
